// Modal "Editar contenido de caja" (Fase 10 / 7.2).
//
// Lista líneas con stepper +/- y botón "Quitar" por línea. Al confirmar,
// dispara `PATCH /cajas/{sscc}` que crea una caja nueva con SSCC nuevo
// (la vieja queda revoked + replaced_by_sscc). El backend re-imprime
// automáticamente. Mostramos al usuario el SSCC nuevo + recordatorio
// "Despega la etiqueta vieja y pega la nueva".
//
// Resuelve con `{reemitida: bool, sscc_nuevo?, sscc_viejo?}` para que el
// caller (modal-estructura) sepa si hay que refrescar.

import { apiPatch } from '../api/client.js';
import { icon } from './icons.js';
import { openAlert } from './modal.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

let _host = null;

function _close() {
  if (_host) { _host.remove(); _host = null; }
}

export function openEditarCajaModal({ caja }) {
  return new Promise((resolve) => {
    _host = document.createElement('div');
    document.body.appendChild(_host);

    // Trabajamos sobre una copia de las líneas para no mutar el árbol
    // de estructura del padre antes de confirmar.
    let lineas = caja.lineas.map(l => ({ ...l }));

    function _lineaRowHTML(linea, idx) {
      const cant = Number.isInteger(linea.cantidad) ? linea.cantidad : linea.cantidad;
      const lote = linea.lote ? ` · lote ${escapeHtml(linea.lote)}` : '';
      return `
        <div data-idx="${idx}" style="
          display:flex; align-items:center; gap:0.5rem;
          padding:0.5rem; border:1px solid var(--c-border); border-radius:var(--radius);
          background:var(--c-surface-2);
        ">
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;">${escapeHtml(linea.descripcion)}</div>
            <div style="color:var(--c-fg-muted);font-size:var(--fs-xs);">EAN ${escapeHtml(linea.barcode || '—')}${lote}</div>
          </div>
          <div class="stepper" style="border:1px solid var(--c-border-strong);">
            <button data-action="dec" data-idx="${idx}" style="width:40px;height:40px;font-size:1.2rem;">−</button>
            <input type="number" inputmode="numeric" min="1" value="${cant}" data-idx="${idx}"
                   style="width:48px;height:40px;font-size:var(--fs-base);" />
            <button data-action="inc" data-idx="${idx}" style="width:40px;height:40px;font-size:1.2rem;">+</button>
          </div>
          <button data-action="del" data-idx="${idx}"
                  style="background:transparent;border:0;color:var(--c-error);cursor:pointer;width:36px;height:36px;display:inline-flex;align-items:center;justify-content:center;"
                  title="Quitar producto">${icon('trash-2', { size: 20 })}</button>
        </div>`;
    }

    function render() {
      const last8 = caja.sscc.slice(-8);
      const lineasHTML = lineas.length === 0
        ? `<div style="color:var(--c-fg-muted);text-align:center;padding:1rem;">
              Caja vacía — no se puede guardar. Cancela o anula la caja desde la vista de estructura.
            </div>`
        : lineas.map((l, i) => _lineaRowHTML(l, i)).join('');

      _host.innerHTML = `
        <div class="scrim fade-in"><div class="modal" style="max-height:90dvh;overflow-y:auto;">
          <h2>${icon('pencil', { size: 22 })} Editar contenido</h2>
          <p style="margin:0;color:var(--c-fg-muted);font-size:var(--fs-sm);">
            Caja actual SSCC <span style="font-family:ui-monospace,monospace;">…${last8}</span>
          </p>
          <div class="lote-missing" style="background:var(--c-partial-bg);border-color:var(--c-partial-border);color:var(--c-partial);">
            <span style="display:inline-flex;">${icon('alert-triangle', { size: 18 })}</span>
            <span>Al guardar se emite una etiqueta nueva con un SSCC distinto.
              Tendrás que despegar la vieja y pegar la nueva.</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:0.4rem;">
            ${lineasHTML}
          </div>
          <div class="actions">
            <button class="btn --ghost" id="ec-cancel">Cancelar</button>
            <button class="btn --primary" id="ec-save" ${lineas.length === 0 ? 'disabled' : ''}>Guardar y reimprimir</button>
          </div>
        </div></div>`;

      _host.querySelector('#ec-cancel').addEventListener('click', () => { _close(); resolve({ reemitida: false }); });
      _host.querySelector('#ec-save').addEventListener('click', save);

      // Steppers / quitar
      _host.querySelectorAll('button[data-action]').forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.idx, 10);
          const action = btn.dataset.action;
          if (action === 'inc') lineas[idx].cantidad += 1;
          else if (action === 'dec') lineas[idx].cantidad = Math.max(1, lineas[idx].cantidad - 1);
          else if (action === 'del') lineas.splice(idx, 1);
          render();
        });
      });
      _host.querySelectorAll('input[type="number"][data-idx]').forEach(inp => {
        inp.addEventListener('change', () => {
          const idx = parseInt(inp.dataset.idx, 10);
          const v = parseFloat(inp.value);
          if (Number.isFinite(v) && v >= 1) lineas[idx].cantidad = v;
          render();
        });
      });
    }

    async function save() {
      if (lineas.length === 0) return;
      const btn = _host.querySelector('#ec-save');
      btn.disabled = true;
      btn.textContent = '⏳ Reimprimiendo…';
      try {
        const res = await apiPatch(`/cajas/${caja.sscc}`, { lineas });
        _close();
        // Modal explícito de reimpresión.
        await openAlert({
          tipo: 'warn',
          titulo: 'Cambia la etiqueta',  // icon irá en la modal misma (alert-triangle)
          detalle: `Despega la etiqueta vieja (SSCC ${caja.sscc}) y pega la nueva (SSCC ${res.sscc}).${res.modo === 'pdf' ? ` PDF guardado en: ${res.etiqueta_url}` : ''}`,
          accion: 'Hecho',
        });
        resolve({ reemitida: true, sscc_nuevo: res.sscc, sscc_viejo: caja.sscc });
      } catch (e) {
        btn.disabled = false;
        btn.textContent = 'Guardar y reimprimir';
        if (e?.status === 401) return;
        await openAlert({
          tipo: 'error',
          titulo: 'No se pudo reimprimir',
          detalle: e?.message || 'Error desconocido',
        });
      }
    }

    render();
  });
}
